import {
  BankOutlined,
  CalendarOutlined,
  ContactsOutlined,
  IdcardOutlined,
  PaperClipOutlined,
  PhoneOutlined,
  ProductOutlined,
  PushpinOutlined,
  ScheduleOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Flex,
  Result,
  Row,
  Space,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import MyAvatar from "../../components/common/Avatar";
import Loading from "../../components/common/Loading";
import MeetingPersonalIcon from "../../components/icons/MeetingPersonal";
import { DepartmentCode } from "../../enum/roles";
import { useGetMyEmployee } from "../../query/HrmQuery";

const { Title, Text } = Typography;

const Personal = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetMyEmployee();
  if (isLoading) {
    return <Loading show={true} />;
  }

  if (!data) {
    return (
      <Flex justify="center" align="center" className="min-h-[80vh]">
        <Result
          status="warning"
          title="Bạn chưa có thông tin nhân viên"
          subTitle="Vui lòng liên hệ phòng Nhân sự để đăng ký thông tin nhân viên cho tài khoản này."
        />
      </Flex>
    );
  }

  const hiredAt = new Date(data.hiredAt);
  const now = new Date();
  const isDirector = data.user.department.departmentCode?.includes(
    DepartmentCode.CEO,
  );
  let workedMonths =
    (now.getFullYear() - hiredAt.getFullYear()) * 12 +
    (now.getMonth() - hiredAt.getMonth());

  if (now.getDate() < hiredAt.getDate()) {
    workedMonths--;
  }
  workedMonths = Math.max(0, workedMonths);

  const workInfoFields = [
    {
      label: "Mã nhân viên",
      icon: <IdcardOutlined />,
      color: "#4947f7",
      value: data.employeeNo,
    },
    {
      label: "Phòng ban",
      icon: <BankOutlined />,
      color: "#4947f7",
      value: data.user.department.departmentName,
    },
    {
      label: "Ngày vào làm",
      icon: <CalendarOutlined />,
      color: "#4947f7",
      value: dayjs(data.hiredAt).format("DD/MM/YYYY"),
    },
    {
      label: "Số ngày phép còn lại",
      icon: <SmileOutlined />,
      color: "#4947f7",
      value: `${data.leaveBalance} ngày`,
      span: 2,
    },
    {
      label: "Thâm niên làm việc",
      icon: <ScheduleOutlined />,
      color: "#4947f7",
      value: `${workedMonths} tháng`,
      span: 2,
    },
  ];

  const contactInfoFields = [
    {
      label: "Ngày sinh",
      icon: <CalendarOutlined />,
      color: "#4947f7",
      value: dayjs(data.born).format("DD/MM/YYYY"),
    },
    {
      label: "Số điện thoại",
      icon: <PhoneOutlined />,
      color: "#4947f7",
      value: data.phone || "Chưa cập nhật",
    },
    {
      label: "Địa chỉ hiện tại",
      icon: <ContactsOutlined />,
      color: "#4947f7",
      span: 2,
      value: data.addressCurrent || "Chưa cập nhật",
    },
    {
      label: "Địa chỉ thường trú",
      icon: <ContactsOutlined />,
      color: "#4947f7",
      span: 2,
      value: data.addressPermanent || "Chưa cập nhật",
    },
  ];

  return (
    <div className="m-3 relative min-h-full">
      <Flex gap="large" className="flex-col lg:flex-row">
        {/* Cột trái: Avatar và Tiện ích */}
        <Flex vertical gap="large" className="w-full lg:w-1/3">
          <Card size="small" className="text-center shadow-sm">
            <Flex vertical align="center" gap="small" className="mt-4 mb-4">
              <MyAvatar
                name={data.user.fullName?.charAt(0).toUpperCase()}
                className="w-24 h-24 shadow-sm"
                userInfo={data.user}
              />
              <Title
                level={3}
                style={{ margin: 0 }}
                className="text-center break-words px-2"
              >
                {data.user.fullName}
                {data.isWorking}
              </Title>
              <Text
                type="secondary"
                className="text-sm sm:text-base break-all px-2 text-center"
              >
                {data.user.email}
              </Text>
              <Space className="mt-2" wrap>
                <Tag
                  color={
                    data.gender === "MALE"
                      ? "blue"
                      : data.gender === "FEMALE"
                        ? "magenta"
                        : "default"
                  }
                  className="px-3 py-1 text-sm rounded-full m-0"
                >
                  {data.gender === "MALE"
                    ? "Nam"
                    : data.gender === "FEMALE"
                      ? "Nữ"
                      : "Khác"}
                </Tag>
                <Tag
                  color="geekblue"
                  className="px-3 py-1 text-sm rounded-full m-0"
                >
                  {data.user.position.positionTitle}
                </Tag>
              </Space>
            </Flex>
          </Card>

          <Flex gap={16} className="flex-col md:flex-row lg:flex-col w-full">
            <Space></Space>
            {!isDirector && (
              <Card
                title={
                  <>
                    <SmileOutlined className="mr-2" />
                    Tiện ích cá nhân
                  </>
                }
                size="small"
                className="w-full"
              >
                <Flex vertical gap="middle">
                  <Button
                    size="large"
                    type="primary"
                    icon={<CalendarOutlined />}
                    onClick={() => navigate("/personal/my-attendance")}
                    block
                  >
                    Chấm công
                  </Button>
                  <Button
                    size="large"
                    type="primary"
                    icon={<PaperClipOutlined />}
                    onClick={() => navigate("/personal/me/leave-requests")}
                    block
                  >
                    Đơn xin nghỉ phép
                  </Button>
                  {data.isOvertime && (
                    <Button
                      size="large"
                      type="primary"
                      icon={<PushpinOutlined />}
                      onClick={() => navigate("/personal/my-overtime-requests")}
                      disabled={!data.isOvertime}
                      block
                    >
                      Đăng ký tăng ca
                    </Button>
                  )}

                  {data.user.position.positionCode?.includes(
                    DepartmentCode.HR,
                  ) && (
                    <Button
                      size="large"
                      type="primary"
                      icon={<ProductOutlined />}
                      onClick={() => navigate("/personal/me/stationery")}
                      block
                    >
                      Đăng ký văn phòng phẩm
                    </Button>
                  )}
                  <Button
                    size="large"
                    type="primary"
                    icon={<MeetingPersonalIcon />}
                    onClick={() => navigate("/personal/my-meeting-schedules")}
                    block
                  >
                    Đặt phòng họp
                  </Button>
                </Flex>
              </Card>
            )}

            {data.user.position.positionCode?.includes("MANAGER") && (
              <Card
                className="w-full"
                title={
                  <>
                    <ContactsOutlined className="mr-2" />
                    Dành cho Quản lý
                  </>
                }
                size="small"
              >
                <Flex vertical gap="middle">
                  <Button
                    size="large"
                    type="primary"
                    danger
                    onClick={() => navigate("/personal/overtime-requests")}
                    block
                  >
                    Quản lý đơn xin tăng ca
                  </Button>
                  <Button
                    size="large"
                    type="primary"
                    danger
                    onClick={() => navigate("/personal/leave-requests")}
                    block
                  >
                    Quản lý đơn xin nghỉ phép
                  </Button>
                </Flex>
              </Card>
            )}
          </Flex>
        </Flex>

        <Flex vertical gap="large" className="w-full lg:w-2/3">
          <Card
            title={
              <>
                <IdcardOutlined className="mr-2" />
                Thông tin công việc
              </>
            }
            size="small"
            className="shadow-sm"
          >
            <Row gutter={[16, 20]} className="p-3">
              {workInfoFields.map((field, index) => (
                <Col
                  key={index}
                  xs={24}
                  sm={12}
                  md={field.span === 3 ? 24 : 12}
                >
                  <Space>
                    <span
                      style={{
                        color: field.color,
                        fontSize: 16,
                      }}
                    >
                      {field.icon}
                    </span>

                    <span>{field.label}</span>
                  </Space>

                  <div>
                    <Text strong className="text-base">
                      {field.value}
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
          <Card
            title={
              <>
                <ContactsOutlined className="mr-2" />
                Thông tin liên hệ & cá nhân
              </>
            }
            size="small"
            className="shadow-sm"
          >
            <Row gutter={[16, 20]} className="p-3">
              {contactInfoFields.map((field, index) => (
                <Col
                  key={index}
                  xs={24}
                  sm={12}
                  md={field.span === 3 ? 24 : 12}
                >
                  <Card size="small" className="h-full">
                    <Space direction="vertical" size={4}>
                      <Space>
                        <Text
                          style={{
                            color: field.color,
                            fontSize: 16,
                          }}
                        >
                          {field.icon}
                        </Text>

                        <Text>{field.label}</Text>
                      </Space>

                      <Text strong className="text-base">
                        {field.value || "-"}
                      </Text>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Flex>
      </Flex>
    </div>
  );
};

export default memo(Personal);
